/**
 * The macOS fleet host in the management UI (plans/2026-10-15-macos-fleet-host.md 9.1):
 *
 *  - the "Add macOS host" flow on the Box Approvals page: the one-time bootstrap token is bound to ONE host id
 *    (purpose macos-tester, 15 minutes, single use) and the page shows the install one-liner with a TTL countdown;
 *  - the Fleet page row of a Mac: state, release, battery/AC, clock health, power-settings drift, thermals, queue,
 *    last job, LAN address and whether the Linux hosts have the deny rules, plus the drain / undrain / restart /
 *    update operations;
 *  - approval of a Mac, which the broker gates on the Linux hosts acknowledging the peer-firewall sync (the
 *    operator may override).
 *
 * Pure functions over the broker's JSON (private/auth fleetHealth.ts FleetBox.macos, macosFleet.ts, joinRoutes.ts)
 * so they are testable without a DOM. The field names mirror what the broker stores: the sanitized HostHeartbeat of
 * internal/deploy/macos-tester/src/protocol.ts (private/auth macosProtocol.ts sanitizeHeartbeat).
 */

export const BROKER_ORIGIN = "https://auth.pure.dev";
export const DEFAULT_MAC_HOST_ID = "pure-macos-1";
/** Matches the broker's host id rule (joinRoutes.ts handleGenerateBootstrapToken). */
export const HOST_ID_RE = /^[a-z0-9_-]{1,64}$/;
/** The macOS bootstrap token lives 15 minutes (joinRoutes.ts). */
export const MACOS_TOKEN_TTL_MS = 15 * 60_000;
/** A Mac sends one heartbeat a minute; silent for this long = no status. */
export const HOST_STALE_MS = 180_000;

// ── Add macOS host ───────────────────────────────────────────────────────────

/** The host id the operator typed, normalised the way every component spells it (lowercase, dots -> underscores). */
export function normalizeHostId(raw: string): string {
  return raw.trim().toLowerCase().replace(/\./g, "_");
}

export function hostIdError(hostId: string): string | null {
  if (!hostId) return "enter the host id of the Mac";
  if (!HOST_ID_RE.test(hostId))
    return "use 1-64 characters: lowercase letters, digits, - and _";
  return null;
}

/**
 * The install one-liner shown on the Box Approvals page. The script is PUBLIC and static (no secret in it); the
 * only credential is the one-time token, which is bound to this host id. It needs root on the Mac.
 */
export function installCommand(
  origin: string,
  token: string,
  hostId: string,
): string {
  return `curl -fsSL ${origin.replace(/\/+$/, "")}/v1/macos-tester/install.sh | sudo bash -s -- --token ${token} --host-id ${hostId}`;
}

export interface Ttl {
  expired: boolean;
  remainingMs: number;
  /** "14:32" while valid, "expired" afterwards. */
  label: string;
}

/** Countdown to a token's `expires_at` (ISO or the broker's "YYYY-MM-DD HH:MM:SS" UTC form). */
export function ttlCountdown(expiresAt: string, nowMs: number): Ttl {
  const iso = expiresAt.includes("T")
    ? expiresAt
    : expiresAt.replace(" ", "T") + "Z";
  const end = Date.parse(iso);
  const remainingMs = Number.isFinite(end) ? end - nowMs : -1;
  if (remainingMs <= 0)
    return { expired: true, remainingMs: 0, label: "expired" };
  const total = Math.ceil(remainingMs / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return {
    expired: false,
    remainingMs,
    label: `${m}:${String(s).padStart(2, "0")}`,
  };
}

// ── kinds on the Box Approvals page ──────────────────────────────────────────

export type BoxKind = "linux" | "macos";

/** From the SPIFFE id prefix, exactly like the broker (boxKind.ts). */
export function boxKindOf(spiffeId: string): BoxKind | null {
  if (spiffeId.startsWith("spiffe://pure.dev/macos-tester/")) return "macos";
  if (spiffeId.startsWith("spiffe://pure.dev/deploy-agent/")) return "linux";
  return null;
}

export function kindLabel(kind: BoxKind | null | undefined): string {
  return kind === "macos"
    ? "macOS host"
    : kind === "linux"
      ? "Linux"
      : "unknown";
}

/** The kind of a pending join request, from the components it asks for. */
export function joinRequestKind(componentsJson: string): BoxKind {
  try {
    const v = JSON.parse(componentsJson) as unknown;
    if (Array.isArray(v) && v.includes("macos-tester")) return "macos";
  } catch {
    /* fall through */
  }
  return "linux";
}

export interface JoinNet {
  iface?: string;
  ipv4?: string[];
  mac?: string | null;
}

/** "192.168.1.139 on en0 (da:e9:8d:81:43:76)" for the operator to compare with the router's client list once. */
export function netSummary(net: JoinNet | null | undefined): string {
  if (!net || !net.ipv4 || net.ipv4.length === 0) return "no address reported";
  const ips = net.ipv4.join(", ");
  return `${ips}${net.iface ? ` on ${net.iface}` : ""}${net.mac ? ` (${net.mac})` : ""}`;
}

export interface PeerSyncMissing {
  box_id: string;
  reason: string;
}

/** The broker answers 409 {needs_override, peer_sync:{missing}} when a Linux host did not acknowledge the deny rules. */
export function peerSyncPrompt(body: unknown): string | null {
  const b = body as {
    needs_override?: boolean;
    peer_sync?: { missing?: PeerSyncMissing[] };
  } | null;
  if (!b || b.needs_override !== true) return null;
  const missing = b.peer_sync?.missing ?? [];
  const who = missing.length
    ? missing.map((m) => `${m.box_id} (${m.reason})`).join(", ")
    : "a Linux host";
  return `${who} did not confirm the firewall rules that deny this Mac. Until they do, the Mac would have the LAN's baseline trust to those hosts. Approve anyway?`;
}

// ── Fleet row ────────────────────────────────────────────────────────────────

export type Tone = "ok" | "info" | "warn" | "bad" | "muted";

export interface Fact {
  label: string;
  value: string;
  tone: Tone;
}

export interface MacosJobRef {
  jobId?: string;
  kind?: string;
  startedAt?: number | null;
  state?: string;
  finishedAt?: number | null;
}

/** private/auth macosProtocol.ts MacosHeartbeat as stored in macos_hosts.status_json. */
export interface MacosStatus {
  serviceVersion?: string | null;
  release?: string | null;
  state?: string;
  stateReasons?: string[];
  powerSettingsDrift?: Array<{ key: string; expected: string; actual: string }>;
  battery?: {
    source?: string | null;
    percent?: number | null;
    chargeState?: string | null;
  };
  thermal?: { pressure?: string | null; throttled?: boolean | null };
  load?: { perCore?: number | null; cpuIdlePct?: number | null };
  clock?: { verdict?: string | null; medianMs?: number | null };
  diskFreeGiB?: number | null;
  pfActive?: boolean | null;
  spireHealthy?: boolean | null;
  /** NATS connected and the host's durable job consumer attached (jobs arrive over NATS). */
  natsHealthy?: boolean | null;
  sandboxProbeOk?: boolean | null;
  queueDepth?: number | null;
  currentJob?: MacosJobRef | null;
  lastJob?: MacosJobRef | null;
  lastError?: string | null;
  net?: JoinNet | null;
}

/** FleetBox.macos (private/auth fleetHealth.ts). */
export interface MacosBoxInfo {
  state: string | null;
  drained: boolean;
  enforce: boolean;
  lan_ips: string[];
  status_at: string | null;
  status: MacosStatus | null;
}

export interface MacosBoxLike {
  connected: boolean;
  last_seen_ago_s: number | null;
  installed_components: Record<string, string>;
  macos?: MacosBoxInfo | null;
}

const REASON_TEXT: Record<string, string> = {
  "operator-drain": "drained by an operator",
  "on-battery": "running on battery",
  "low-battery": "battery below 30%",
  "critical-battery": "battery critically low, shutting down",
  "clock-skew": "clock skew (majority of time sources disagree)",
  "power-settings-drift": "power settings drifted from the enforced set",
  "pf-inactive": "pf firewall is not active",
  "sandbox-probe-failed": "sandbox self-test failed",
  "spire-unhealthy": "SPIRE agent unhealthy",
  "nats-unhealthy": "NATS connection or job consumer down",
  "disk-low": "disk space low",
  "peer-sync-pending": "waiting for the Linux hosts to learn its address",
  "toolchain-mismatch": "toolchain differs from the release pins",
  "last-job-failed-closed": "last job failed closed",
  "shutting-down": "shutting down",
  "heartbeat-payload-missing": "heartbeat carried no status",
  "heartbeat-state-unrecognised": "heartbeat state not recognised",
};

export function reasonText(code: string): string {
  return REASON_TEXT[code] ?? code;
}

export type HostPhase =
  "offline" | "stale" | "busy" | "accepting" | "draining" | "degraded";

export interface MacosHostView {
  phase: HostPhase;
  /** Short badge text. */
  label: string;
  tone: Tone;
  /** Plain-language reasons the host is not accepting jobs (empty when it is). */
  reasons: string[];
  facts: Fact[];
  drained: boolean;
  /** The next drain action the button offers. */
  drainAction: "drain" | "undrain";
}

function parseTs(s: string | null): number | null {
  if (!s) return null;
  const t = Date.parse(s.includes("T") ? s : s.replace(" ", "T") + "Z");
  return Number.isFinite(t) ? t : null;
}

function fmtClock(c: MacosStatus["clock"]): Fact {
  if (!c || !c.verdict)
    return { label: "Clock", value: "unknown", tone: "muted" };
  const off =
    typeof c.medianMs === "number"
      ? Math.abs(c.medianMs) >= 1000
        ? `${(c.medianMs / 1000).toFixed(1)} s`
        : `${Math.round(c.medianMs)} ms`
      : "";
  if (c.verdict === "ok")
    return {
      label: "Clock",
      value: `in sync${off ? ` (median ${off})` : ""}`,
      tone: "ok",
    };
  if (c.verdict === "skewed")
    return {
      label: "Clock",
      value: `SKEWED${off ? ` (median ${off})` : ""}`,
      tone: "bad",
    };
  return { label: "Clock", value: c.verdict, tone: "warn" };
}

function fmtPower(b: MacosStatus["battery"]): Fact {
  if (!b || !b.source)
    return { label: "Power", value: "unknown", tone: "muted" };
  const pct =
    typeof b.percent === "number" && b.percent >= 0 ? `${b.percent}%` : "?%";
  if (b.source === "ac" || b.source === "ups")
    return {
      label: "Power",
      value: `${b.source === "ac" ? "AC" : "UPS"} · battery ${pct}${b.chargeState ? ` (${b.chargeState})` : ""}`,
      tone: "ok",
    };
  return {
    label: "Power",
    value: `ON BATTERY ${pct}`,
    tone: typeof b.percent === "number" && b.percent < 30 ? "bad" : "warn",
  };
}

function fmtBool(
  label: string,
  v: boolean | null | undefined,
  good: string,
  bad: string,
): Fact {
  if (v === null || v === undefined)
    return { label, value: "unknown", tone: "muted" };
  return { label, value: v ? good : bad, tone: v ? "ok" : "bad" };
}

function fmtJob(j: MacosJobRef | null | undefined): string {
  if (!j || !j.jobId) return "none";
  const id = j.jobId.slice(0, 8);
  return `${j.state ? `${j.state} ` : ""}${j.kind ? `${j.kind.replace("addon-", "").replace("-macos", "")} ` : ""}${id}`.trim();
}

/** Everything the Fleet row shows for a Mac, from the broker's box record. */
export function macosHostView(box: MacosBoxLike, nowMs: number): MacosHostView {
  const m = box.macos ?? null;
  const st = m?.status ?? null;
  const drained = m?.drained === true;
  const drainAction: "drain" | "undrain" = drained ? "undrain" : "drain";
  const statusAt = parseTs(m?.status_at ?? null);
  const fresh = statusAt !== null && nowMs - statusAt <= HOST_STALE_MS;
  const facts: Fact[] = [];

  const release =
    st?.release ?? box.installed_components["macos-tester"] ?? null;
  facts.push({
    label: "Release",
    value: `${release ?? "unknown"}${st?.serviceVersion ? ` · service ${st.serviceVersion}` : ""}`,
    tone: release ? "ok" : "muted",
  });
  facts.push(fmtPower(st?.battery));
  facts.push(fmtClock(st?.clock));
  const drift = st?.powerSettingsDrift ?? [];
  facts.push(
    drift.length === 0
      ? {
          label: "Power settings",
          value: "as enforced",
          tone: st ? "ok" : "muted",
        }
      : {
          label: "Power settings",
          value: `DRIFT ${drift.map((d) => `${d.key} ${d.actual}≠${d.expected}`).join(", ")}`,
          tone: "warn",
        },
  );
  const th = st?.thermal;
  facts.push(
    !th || !th.pressure
      ? { label: "Thermal", value: "unknown", tone: "muted" }
      : {
          label: "Thermal",
          value: `${th.pressure}${th.throttled ? " · throttled" : ""}`,
          tone:
            th.throttled ||
            (th.pressure !== "nominal" && th.pressure !== "unknown")
              ? "warn"
              : "ok",
        },
  );
  // The Mac's own queue (jobs arrive over NATS, not through the broker): what its heartbeat reports.
  const depth = st?.queueDepth ?? null;
  facts.push({
    label: "Queue",
    value:
      depth === null
        ? "unknown"
        : `${depth} queued${st?.currentJob ? " · 1 running" : ""}`,
    tone: "info",
  });
  facts.push({
    label: "Current job",
    value: fmtJob(st?.currentJob),
    tone: st?.currentJob ? "info" : "muted",
  });
  facts.push({
    label: "Last job",
    value: fmtJob(st?.lastJob),
    tone: st?.lastJob?.state === "failed" ? "warn" : "muted",
  });
  if (typeof st?.diskFreeGiB === "number")
    facts.push({
      label: "Disk free",
      value: `${Math.round(st.diskFreeGiB)} GiB`,
      tone: st.diskFreeGiB < 20 ? "warn" : "ok",
    });
  facts.push(fmtBool("pf firewall", st?.pfActive, "active", "NOT active"));
  facts.push(fmtBool("SPIRE agent", st?.spireHealthy, "healthy", "UNHEALTHY"));
  facts.push(fmtBool("NATS", st?.natsHealthy, "connected", "DOWN"));
  facts.push(fmtBool("Sandbox probe", st?.sandboxProbeOk, "ok", "FAILED"));
  facts.push({
    label: "LAN address",
    value: m && m.lan_ips.length > 0 ? m.lan_ips.join(", ") : "not reported",
    tone: m && m.lan_ips.length > 0 ? "info" : "warn",
  });
  facts.push(
    !m
      ? { label: "Peer firewall", value: "unknown", tone: "muted" }
      : m.enforce
        ? {
            label: "Peer firewall",
            value: "denied on the Linux hosts (learned address)",
            tone: "ok",
          }
        : { label: "Peer firewall", value: "NOT enforced yet", tone: "warn" },
  );
  if (st?.lastError)
    facts.push({ label: "Last error", value: st.lastError, tone: "bad" });

  // phase
  const reasons = (st?.stateReasons ?? []).map(reasonText);
  if (!box.connected) {
    return {
      phase: "offline",
      label: "offline",
      tone: "bad",
      reasons: [
        `the agent has not connected for ${box.last_seen_ago_s ?? "?"}s`,
      ],
      facts,
      drained,
      drainAction,
    };
  }
  if (!fresh) {
    return {
      phase: "stale",
      label: "no status",
      tone: "warn",
      reasons: ["no host heartbeat in the last 3 minutes"],
      facts,
      drained,
      drainAction,
    };
  }
  if (drained || st?.state === "draining") {
    const r =
      drained && !reasons.includes(reasonText("operator-drain"))
        ? [reasonText("operator-drain"), ...reasons]
        : reasons;
    return {
      phase: "draining",
      label: "drained",
      tone: "warn",
      reasons: r,
      facts,
      drained,
      drainAction,
    };
  }
  if (st?.state === "degraded" || (st?.state && st.state !== "ok"))
    return {
      phase: "degraded",
      label: "degraded",
      tone: "warn",
      reasons,
      facts,
      drained,
      drainAction,
    };
  if (st?.currentJob)
    return {
      phase: "busy",
      label: "running a job",
      tone: "info",
      reasons: [],
      facts,
      drained,
      drainAction,
    };
  return {
    phase: "accepting",
    label: "accepting jobs",
    tone: "ok",
    reasons: [],
    facts,
    drained,
    drainAction,
  };
}

export const MACOS_OPS = {
  drain: {
    op: "drain",
    confirm:
      "Drain this Mac? It finishes its current job and takes no new ones.",
  },
  undrain: { op: "undrain", confirm: null },
  restart: {
    op: "restart-service",
    confirm: "Restart the macos-tester service on this Mac?",
  },
  update: { op: "update-component", confirm: null },
  purge: {
    op: "purge-jobs",
    confirm: "Delete leftover job directories on this Mac?",
  },
} as const;

export type MacosOpKey = keyof typeof MACOS_OPS;

/** The body POSTed to /fleet/macos/op for a Fleet-row button. */
export function macosOpBody(
  boxId: string,
  key: MacosOpKey,
): { box_id: string; op: string; args: Record<string, unknown> } {
  switch (key) {
    case "restart":
      return {
        box_id: boxId,
        op: "restart-service",
        args: { unit: "dev.pure.macos-tester" },
      };
    case "update":
      return {
        box_id: boxId,
        op: "update-component",
        args: { component: "macos-tester" },
      };
    case "drain":
      return {
        box_id: boxId,
        op: "drain",
        args: { reason: "drained from the Fleet page" },
      };
    default:
      return { box_id: boxId, op: MACOS_OPS[key].op, args: {} };
  }
}

/** Banner lines for the Fleet page: one per Mac that is not accepting jobs. */
export function macosBannerLines(
  boxes: Array<MacosBoxLike & { hostname: string; kind?: string | null }>,
  nowMs: number,
): string[] {
  const out: string[] = [];
  for (const b of boxes) {
    if (b.kind !== "macos") continue;
    const v = macosHostView(b, nowMs);
    if (v.phase === "accepting" || v.phase === "busy") continue;
    out.push(
      `${b.hostname}: ${v.label}${v.reasons.length ? ` — ${v.reasons.join("; ")}` : ""}`,
    );
  }
  return out;
}
